/**
 * Générateur — Automatisme : appliquer les critères de divisibilité par
 * 2, 3, 5, 9.
 * Attendu ID : 1224 | Niveau : 3ème | Domaine : Multiples et diviseurs
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 11, ligne 788).
 *
 * ── Périmètre (cadrage programme-maths 2026-09-12, mode auto) ─────────────
 * Les QUATRE critères de la liste, et eux seuls (10 hors intitulé, 4, 6, 7
 * exclus, jamais « par 2 et par 3 à la fois ») : 3 et 9 deux fois sur trois
 * (le neuf : l'objectif 5e l. 506 automatisé, gen_700), 2 et 5 en entretien
 * (1039, 5e). Nombres de 3 ou 4 chiffres CONSTRUITS par leurs chiffres
 * (jamais tirés au hasard), statut re-vérifié par n % d (throw). Cas vicieux
 * obligatoires : divisible par 3 mais pas par 9 ; finit par 9 sans être
 * divisible par 9 ; finit par 5 interrogé sur 2 ; pair interrogé sur 5.
 * Banque MathsMentales (S. Cogez, Apache 2.0, relevé 2026-09-12) : 5ND2
 * options 0-3 (2, 3, 5, 9 ; 3 à 6 chiffres) ; retenu 3 à 4 chiffres (1039),
 * cinq chiffres et variante « N'EST PAS » (negated) refusés (choix mode
 * auto, À ARBITRER).
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Série flash » (flash_tableau_vf) : quatre lignes « 4 725 est
 *     divisible par 3. », une par diviseur, au moins un cas vicieux, au
 *     moins une vraie et une fausse.
 *   Carte 2 « Repérer le nombre divisible » (reperer_qcm_unique, nom de
 *     1039) : « Quel nombre est divisible par 9 ? », QUATRE nombres de même
 *     longueur, exactement un divisible (recompté, throw) ; contenu.sousType
 *     = le diviseur ; familles par diviseur.
 *   Carte 3 « Complète avec un chiffre » (completer_saisie, nom de 700) :
 *     « Quel est le plus petit chiffre à écrire à la place de ◻ pour que
 *     47◻ soit divisible par 9 ? », 3 et 9 seulement (2 et 5 triviaux),
 *     unicité par « le plus petit » (gen_700).
 *
 * ── Erreurs (familles nommées ; aucun item DEPP) ─────────────────────────
 *   par_3_donc_par_9      : somme 12, 15, 21 : divisible par 3 pris pour 9,
 *                           PRINCIPALE (l'inverse « par 9 donc par 3 » est
 *                           VRAI : une ligne V du Vrai/Faux) ;
 *   finit_par_le_diviseur : 4 719 finit par 9, 253 finit par 3 : le critère
 *                           des unités transporté à 3 et 9, NEUF ;
 *   contient_le_chiffre   : un 5 (ou un chiffre pair) hors des unités (1039) ;
 *   pair_pris_pour_multiple : pair interrogé sur 5 (1039) ;
 *   finit_par_5           : finit par 5 interrogé sur 2 (1039) ;
 *   quelconque            : ni divisible ni piégeux (appoint).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie,
} from '../../../utils';

const ATTENDU_ID = 1224;
const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_QCM = 'Repérer le nombre divisible';
const L_COMPLETER = 'Complète avec un chiffre';

// ─── Le domaine ───────────────────────────────────────────────────────────

const DIVISEURS = [2, 3, 5, 9];
const somme = (n) => String(n).split('').reduce((s, c) => s + Number(c), 0);
/** Assert : le statut annoncé est le vrai (throw sinon). */
const verifier = (n, d, oui) => { if ((n % d === 0) !== oui) throw new Error(`gen_1224 : ${n} et ${d}`); return n; };

/** Un nombre de `longueur` chiffres (3 ou 4), sans zéro de tête, aux unités imposées si `unites` est donné. */
const chiffres = (longueur, unites = null) => {
    const c = [randInt(1, 9)];
    for (let i = 1; i < longueur - 1; i++) c.push(randInt(0, 9));
    c.push(unites ?? randInt(0, 9));
    return c;
};
const nombre = (c) => Number(c.join(''));

/**
 * Un nombre divisible (ou non) par d, construit par ses chiffres ; `cas`
 * choisit le piège : pour 2/5 le chiffre des unités, pour 3/9 la somme.
 *   par 2 : oui → unités paires ; non → 'finit_par_5' (unités 5), 'contient_pair'
 *           (un chiffre pair hors des unités) ou impair.
 *   par 5 : oui → unités 0 ou 5 ; non → 'pair' (unités paires ≠ 0) ou
 *           'contient_5' (un 5 hors des unités) ou impair sans 5.
 *   par 3 : oui → somme multiple de 3 ; non → 'finit_par_3' ou quelconque.
 *   par 9 : oui → somme multiple de 9 ; non → 'par_3_pas_9' (somme multiple
 *           de 3, pas de 9), 'finit_par_9', ou quelconque.
 */
const construire = (d, oui, cas = null, longueur = randInt(3, 4)) => {
    for (let essai = 0; essai < 200; essai++) {
        let c;
        if (d === 2) {
            c = chiffres(longueur, oui ? randomChoice([0, 2, 4, 6, 8]) : cas === 'finit_par_5' ? 5 : randomChoice([1, 3, 7, 9]));
            if (cas === 'contient_pair') { const pos = randInt(0, longueur - 2); c[pos] = randomChoice(pos === 0 ? [2, 4, 6, 8] : [0, 2, 4, 6, 8]); }
        } else if (d === 5) {
            if (oui) c = chiffres(longueur, randomChoice([0, 5]));
            else if (cas === 'pair') c = chiffres(longueur, randomChoice([2, 4, 6, 8]));
            else if (cas === 'contient_5') { c = chiffres(longueur, randomChoice([1, 2, 3, 4, 6, 7, 8, 9])); c[randInt(0, longueur - 2)] = 5; }
            else c = chiffres(longueur, randomChoice([1, 3, 7, 9]));
            if (cas !== 'contient_5' && !oui && c.slice(0, -1).includes(5)) continue;
        } else {
            // 3 et 9 : on ajuste le chiffre des unités à la somme voulue
            c = chiffres(longueur);
            const s = somme(nombre(c.slice(0, -1).concat([0])));
            const bons = [];
            for (let u = 0; u <= 9; u++) {
                const t = s + u;
                if (oui) { if (t % d === 0) bons.push(u); continue; }
                if (t % d === 0) continue;
                if (cas === 'par_3_pas_9') { if (t % 3 === 0) bons.push(u); continue; }
                if (cas === 'finit_par_le_diviseur') { if (u === d) bons.push(u); continue; }
                if (d === 9 && t % 3 === 0) continue; // quelconque : ni par 3 ni par 9
                bons.push(u);
            }
            if (!bons.length) continue;
            c[longueur - 1] = randomChoice(bons);
        }
        if (c[0] === 0) continue;
        const n = nombre(c);
        if (String(n).length !== longueur) continue;
        return verifier(n, d, oui);
    }
    return oui ? verifier(d * 111, d, true) : verifier(d * 111 + 1, d, false);
};

const EXPLICATIONS = {
    par_3_donc_par_9: 'Divisible par 3 ne veut pas dire divisible par 9 : 12 est dans la table de 3, pas dans celle de 9. Pour 9, la somme des chiffres doit être 9, 18 ou 27.',
    finit_par_le_diviseur: 'Pour 3 et 9, le chiffre des unités ne dit rien : je fais la somme des chiffres. Pour 2 et 5, c\'est le chiffre des unités qui décide.',
    contient_le_chiffre: 'Un 5 au milieu ne compte pas : pour 5, seul le chiffre des unités compte (0 ou 5).',
    pair_pris_pour_multiple: 'Pair, c\'est divisible par 2, pas par 5 : pour 5 il faut finir par 0 ou 5.',
    finit_par_5: 'Finir par 5, c\'est impair : pas divisible par 2.',
    quelconque: 'Je vérifie avec le bon critère : unités pour 2 et 5, somme des chiffres pour 3 et 9.',
    regle: 'Par 2 : unités 0, 2, 4, 6, 8. Par 5 : unités 0 ou 5. Par 3 : somme des chiffres dans la table de 3. Par 9 : somme des chiffres dans la table de 9.',
};

// ─── Carte 1 : Série flash (Vrai/Faux) ────────────────────────────────────

/** Une ligne pour un diviseur : le cas vicieux si demandé, sinon vrai ou faux au hasard. */
const ligne = (d, vicieux) => {
    if (vicieux) {
        const cas = { 2: 'finit_par_5', 5: randomChoice(['pair', 'contient_5']), 3: 'finit_par_le_diviseur', 9: randomChoice(['par_3_pas_9', 'finit_par_le_diviseur']) }[d];
        const code = { finit_par_5: 'finit_par_5', pair: 'pair_pris_pour_multiple', contient_5: 'contient_le_chiffre', finit_par_le_diviseur: 'finit_par_le_diviseur', par_3_pas_9: 'par_3_donc_par_9' }[cas];
        return { n: construire(d, false, cas), d, vraie: false, code };
    }
    const vraie = randomChoice([true, false]);
    return { n: construire(d, vraie), d, vraie, code: vraie ? null : 'quelconque' };
};

const genFlash = () => {
    for (let essai = 0; essai < 100; essai++) {
        const ordre = shuffle([...DIVISEURS]);
        const iVicieux = randInt(0, 3);
        const lignes = ordre.map((d, i) => ligne(d, i === iVicieux));
        if (new Set(lignes.map((l) => l.n)).size < 4) continue;
        if (!lignes.some((l) => l.vraie) || !lignes.some((l) => !l.vraie)) continue;
        const payload = makeVraiFaux(lignes.map((l) => `${l.n} est divisible par ${l.d}.`), lignes.map((l) => (l.vraie ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_FLASH));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { lignes: lignes.map(({ n, d, vraie }) => ({ n, d, vraie })), distracteurs: lignes.filter((l) => l.code).map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli vérifié : 4 725 par 3 V ; 4 725 par 9 F (somme 18 : vrai !) → 4 716 par 9 V ; 253 par 3 F ; 1 245 par 2 F.
    const lignes = [{ n: 4725, d: 3, vraie: true }, { n: 4716, d: 9, vraie: true }, { n: 253, d: 3, vraie: false }, { n: 1251, d: 2, vraie: false }];
    lignes.forEach((l) => verifier(l.n, l.d, l.vraie));
    const payload = makeVraiFaux(lignes.map((l) => `${l.n} est divisible par ${l.d}.`), lignes.map((l) => (l.vraie ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_FLASH));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { lignes, distracteurs: [{ code: 'finit_par_le_diviseur' }, { code: 'finit_par_5' }] };
    return payload;
};

// ─── Carte 2 : Repérer le nombre divisible (QCM) ──────────────────────────

/** Les trois distracteurs d'un diviseur : deux pièges nommés et un quelconque, tous NON divisibles par d, même longueur. */
const distracteurs = (d, longueur) => {
    const cas = {
        2: [['finit_par_5', 'finit_par_5'], ['contient_le_chiffre', 'contient_pair'], ['quelconque', null]],
        5: [['pair_pris_pour_multiple', 'pair'], ['contient_le_chiffre', 'contient_5'], ['quelconque', null]],
        3: [['finit_par_le_diviseur', 'finit_par_le_diviseur'], ['quelconque', null], ['quelconque', null]],
        9: [['par_3_donc_par_9', 'par_3_pas_9'], ['finit_par_le_diviseur', 'finit_par_le_diviseur'], ['quelconque', null]],
    }[d];
    return cas.map(([code, c]) => ({ code, n: construire(d, false, c, longueur) }));
};

const genQcm = () => {
    for (let essai = 0; essai < 100; essai++) {
        const d = randomChoice([3, 9, 3, 9, 2, 5]);
        const longueur = randInt(3, 4);
        const bon = construire(d, true, null, longueur);
        const faux = distracteurs(d, longueur);
        const choix = [String(bon), ...faux.map((f) => String(f.n))];
        if (new Set(choix).size !== 4) continue;
        if (choix.filter((c) => Number(c) % d === 0).length !== 1) throw new Error('gen_1224 : QCM à plusieurs divisibles');
        const payload = makeQcm(choix, String(bon), `<p><b>Quel nombre est divisible par ${d} ?</b></p>`, opts(L_QCM));
        payload.contenu = { ...payload.contenu, sousType: String(d), explications: EXPLICATIONS };
        payload._meta.params = { d, bon, distracteurs: faux.map(({ code }) => ({ code })) };
        return payload;
    }
    // Repli vérifié : par 9 → 4 716 ; 4 725 (somme 18 : divisible !) non → 4 713 (somme 15 : par 3 pas 9), 4 719 (finit par 9), 4 702.
    const payload = makeQcm(['4716', '4713', '4719', '4702'], '4716', '<p><b>Quel nombre est divisible par 9 ?</b></p>', opts(L_QCM));
    payload.contenu = { ...payload.contenu, sousType: '9', explications: EXPLICATIONS };
    payload._meta.params = { d: 9, bon: 4716, distracteurs: [{ code: 'par_3_donc_par_9' }, { code: 'finit_par_le_diviseur' }, { code: 'quelconque' }] };
    return payload;
};

// ─── Carte 3 : Complète avec un chiffre (saisie) ──────────────────────────

const genCompleter = () => {
    const d = randomChoice([3, 9]);
    const longueur = randInt(3, 4);
    const c = chiffres(longueur);
    const pos = randInt(1, longueur - 1); // jamais le chiffre de tête (0 y ferait un zéro de tête)
    const reste = c.reduce((s, x, i) => (i === pos ? s : s + x), 0);
    const plusPetit = (d - (reste % d)) % d;
    const gabarit = c.map((x, i) => (i === pos ? '◻' : String(x))).join('');
    verifier(Number(gabarit.replace('◻', String(plusPetit))), d, true);
    for (let x = 0; x < plusPetit; x++) verifier(Number(gabarit.replace('◻', String(x))), d, false);
    const payload = makeSaisie(String(plusPetit), `<p><b>Quel est le plus petit chiffre à écrire à la place de ◻ pour que le nombre ${gabarit} soit divisible par ${d} ?</b></p>`, opts(L_COMPLETER));
    payload.contenu = { ...payload.contenu, sousType: String(d), explications: { regle: EXPLICATIONS.regle, finit_par_le_diviseur: EXPLICATIONS.finit_par_le_diviseur } };
    payload._meta.params = { d, gabarit, plusPetit };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre nombres de même longueur (3 ou 4 chiffres), exactement un divisible par d (recompté, throw) ; contenu.sousType = d',
        'statut construit par les chiffres (unités pour 2 et 5, somme pour 3 et 9), re-vérifié par n % d',
        'diviseurs 2, 3, 5, 9 seulement ; 3 et 9 deux fois sur trois',
    ],
    qcm_unique: {
        familles: {
            par_3_donc_par_9: 'somme multiple de 3, pas de 9 (interrogé sur 9), la principale',
            finit_par_le_diviseur: 'finit par 3 (ou 9) sans être divisible : le critère des unités transporté',
            contient_le_chiffre: 'un 5 hors des unités (sur 5), un chiffre pair hors des unités (sur 2)',
            pair_pris_pour_multiple: 'pair, unités ≠ 0 (sur 5)',
            finit_par_5: 'finit par 5 (sur 2)',
            quelconque: 'ni divisible ni piégeux',
        },
    },
    vf_tableau_vf: 'quatre lignes, une par diviseur, un cas vicieux, au moins une vraie et une fausse',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_tableau_vf: genFlash,
    reperer_qcm_unique: genQcm,
    completer_saisie: genCompleter,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_tableau_vf', label: 'Vrai/Faux' }] },
    { id: 'reperer', label: L_QCM, formatsList: [{ key: 'reperer_qcm_unique', label: 'QCM' }] },
    { id: 'completer', label: L_COMPLETER, formatsList: [{ key: 'completer_saisie', label: 'Saisie' }] },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
